import {
  Autocomplete,
  MenuItem,
  TextField,
  createFilterOptions,
} from '@mui/material';
import { TrainingType } from '@strength/domain';
import { forwardRef, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 } from 'uuid';
import { ErrorIcon } from '../icons/error-icon';

export function TrainingTypeSelect({
  label,
  trainingTypes,
  selectedType,
  onChange,
  hasError,
  allowCreate = false,
  disabled = false,
}: {
  label: string;
  trainingTypes: TrainingType[];
  selectedType: TrainingType | null;
  onChange: (selectedType: TrainingType | null) => void;
  hasError?: (id: string) => boolean;
  allowCreate?: boolean;
  disabled?: boolean;
}) {
  const { t } = useTranslation();

  const [createdTypes, setCreatedTypes] = useState<TrainingType[]>([]);

  const allOptions = useMemo(
    () =>
      [...trainingTypes, ...createdTypes].sort((a, b) =>
        a.name.localeCompare(b.name),
      ),
    [trainingTypes, createdTypes],
  );

  const filter = useMemo(() => createFilterOptions<TrainingType>(), []);

  const filterOptions = useCallback(
    (
      options: TrainingType[],
      state: {
        inputValue: string;
        getOptionLabel: (option: TrainingType) => string;
      },
    ) => {
      const filtered: TrainingType[] = filter(options, state);

      const { inputValue } = state;
      const isExisting = allOptions.some(
        (option) => inputValue === option.name,
      );
      if (allowCreate && inputValue !== '' && !isExisting) {
        filtered.push({
          id: 'create',
          name: t('createNewType', { type: inputValue }),
          inputValue,
        } as TrainingType);
      }

      return filtered;
    },
    [filter, allOptions, t, allowCreate],
  );

  const handleChange = useCallback(
    (e: React.SyntheticEvent, value: TrainingType | null) => {
      let finalValue = value;
      if (value?.id === 'create') {
        const inputValue =
          'inputValue' in value ? value.inputValue : value.name;
        const newType: TrainingType = {
          id: v4(),
          name: inputValue as string,
        };
        setCreatedTypes((prev) => [...prev, newType]);
        finalValue = newType;
      }

      onChange(finalValue);
    },
    [onChange],
  );

  return (
    <Autocomplete
      options={allOptions}
      value={selectedType}
      disabled={disabled}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      forcePopupIcon
      getOptionLabel={(option) => option.name}
      filterOptions={filterOptions}
      onChange={handleChange}
      renderOption={(props, option) => (
        <TrainingTypeItem
          value={option.id}
          {...props}
          type={option}
          hasError={hasError}
          isCreateOption={option.id === 'create'}
        />
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={t('selectType')}
          sx={{ mt: 0 }}
          disabled={disabled}
        />
      )}
    />
  );
}

interface TrainingTypeItemProps {
  type: TrainingType;
  value: string;
  hasError?: (profileId: string) => boolean;
  isCreateOption?: boolean;
}

// forwardRef is needed on custom MenuItems for Select to work properly
// https://stackoverflow.com/questions/68378474/mui-select-custom-menuitem-not-working-properly
const TrainingTypeItem = forwardRef<HTMLLIElement, TrainingTypeItemProps>(
  ({ type, hasError, isCreateOption, ...rest }, ref) => {
    return (
      <MenuItem {...rest} ref={ref}>
        <div className="flex items-center">
          <span className={isCreateOption ? 'font-medium text-blue-600' : ''}>
            {type.name}
          </span>
          {!isCreateOption && hasError?.(type.id) && (
            <ErrorIcon className="ml-2" />
          )}
        </div>
      </MenuItem>
    );
  },
);
